import React from 'react';
import { money, money0 } from '../../utils/money';
import './MeetingMarketingReport.css';

const integer = value => Number(value || 0).toLocaleString('en-MY');
const percent = value => (value == null ? '—' : `${Number(value).toLocaleString('en-MY', { maximumFractionDigits: 1 })}%`);
const day = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return value || '—';
  return new Intl.DateTimeFormat('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })
    .format(new Date(`${value}T00:00:00`));
};

function Progress({ value }) {
  if (value == null) return null;
  return (
    <span className="mmr-progress" aria-hidden="true">
      <i style={{ width: `${Math.min(Math.max(Number(value) || 0, 0), 100)}%` }} />
    </span>
  );
}

function Metric({ label, value, detail, progress }) {
  return (
    <div className="mmr-metric">
      <span className="mmr-metric-label">{label}</span>
      <strong>{value}</strong>
      <span className="mmr-metric-detail">{detail}</span>
      <Progress value={progress} />
    </div>
  );
}

export default function MeetingMarketingReport({ report }) {
  if (!report || !report.kpis) return null;
  const k = report.kpis;
  const sourceLabel = report.source === 'frozen'
    ? 'Meeting snapshot'
    : report.source === 'reconstructed'
      ? 'Historical reconstruction'
      : 'Live data';

  return (
    <section id="meeting-marketing" className="mmr" aria-labelledby="meeting-marketing-title">
      <header className="mmr-head">
        <div>
          <span className="mmr-eyebrow">Team pulse</span>
          <h2 id="meeting-marketing-title">Marketing Report</h2>
          <p>Month to date through {day(report.through)} · meeting day excluded</p>
        </div>
        <span className={`mmr-source mmr-source-${report.source || 'live'}`}>{sourceLabel}</span>
      </header>

      <div className="mmr-kpis">
        <Metric
          label="Enquiries"
          value={integer(k.enquiries)}
          detail={k.lead_target > 0 ? `${percent(k.lead_progress_pct)} of ${integer(k.lead_target)} target` : 'Target not set'}
          progress={k.lead_progress_pct}
        />
        <Metric
          label="Ad spend"
          value={money0(k.spend, 'RM 0')}
          detail={k.budget > 0 ? `${percent(k.budget_pct)} of ${money0(k.budget)} budget` : 'Budget not set'}
          progress={k.budget_pct}
        />
        <Metric
          label="Cost / enquiry"
          value={money(k.cost_per_enquiry)}
          detail={`${integer(k.junk_leads)} junk ${Number(k.junk_leads) === 1 ? 'lead' : 'leads'} excluded`}
        />
        <Metric
          label="Done deals"
          value={integer(k.done_deals)}
          detail={k.cost_per_done_deal == null ? 'Event spaces only' : `${money(k.cost_per_done_deal)} per deal`}
        />
        <Metric
          label="Top channel"
          value={k.top_channel?.channel || '—'}
          detail={k.top_channel ? `${integer(k.top_channel.count)} enquiries` : 'No enquiries yet'}
        />
      </div>

      <div className="mmr-table-wrap" tabIndex="0" aria-label="Marketing results by project">
        <table className="mmr-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Enquiries</th>
              <th>Lead target</th>
              <th>Spend</th>
              <th>Budget</th>
              <th>Cost / enquiry</th>
              <th>Won</th>
            </tr>
          </thead>
          <tbody>
            {(report.per_project || []).map(row => (
              <tr key={row.project}>
                <th scope="row">{row.project}</th>
                <td>{integer(row.enquiries)}</td>
                <td>{row.lead_target > 0 ? `${integer(row.lead_target)} · ${percent(row.lead_progress_pct)}` : '—'}</td>
                <td>{money0(row.spend, 'RM 0')}</td>
                <td>{row.budget > 0 ? `${money0(row.budget)} · ${percent(row.budget_pct)}` : '—'}</td>
                <td>{money(row.cost_per_enquiry)}</td>
                <td>{integer(row.won)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
